<script setup lang="ts">
import { computed, inject, onMounted, ref, Ref, toRefs } from 'vue';
import InputNumber from 'primevue/inputnumber';
import MultiSelect from 'primevue/multiselect';
import Message from 'primevue/message';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf, helpers, url } from '@vuelidate/validators';

import OrganizationContractGroupEvents from './OrganizationContractGroupEvents.vue';
import OrganizationContractNoShow from './OrganizationContractNoShow.vue';
import { useOrganizationContractsStore, useTherapyStore } from '@/store';
import { ContractDetailInterface, DropdownOptionInterface, GroupEventInterface } from '@/types';
import { useDateTime } from '@/composables';
import { CONTRACT_ENUMS } from '@/enum';
import OrganizationContractEvaluations from './OrganizationContractEvaluations.vue';

interface Props {
  contract: ContractDetailInterface;
  loading: boolean;
  scope: string;
}
const contractStore = useOrganizationContractsStore();
const therapyStore = useTherapyStore();

const props = defineProps<Props>();
const { contract, loading, scope } = toRefs(props);
const canEdit = contractStore.canEditOrCreateContracts;
const caseloadManagementTypes = ref<DropdownOptionInterface[]>();
const eventsRemovedByDateEdit = ref<number>(0);
const eventsRemovedForNewContract = ref<number>(0);
const openServiceDateNotice = ref<boolean>(false);
const caseloadsLoading = ref<boolean>(false);
const selectedMainForm = inject<Ref<CONTRACT_ENUMS.FORMS>>('selectedForm', ref(CONTRACT_ENUMS.FORMS.FLAT_FEE));
const originalPaymentStartDate = ref<Date>();
const originalPaymentEndDate = ref<Date>();

const toolTipText = {
  contract: 'Date range in which service providers can deliver services.',
  service: 'Date range in which service providers can schedule and document events.',
  payment: 'Date range in which invoices will be issued.',
};

onMounted(() => {
  contractStore.getSalesReps();
  cleanSalerep();
  getCaseloadManagementTypes();
});

const cleanSalerep = () => {
  const salesrep = contract.value.salesrep;
  const editType = contractStore.editType;
  const resetEditTypes = [CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT];

  if (!salesrep?.active && !resetEditTypes.includes(editType)) {
    contract.value.salesrep = undefined;
  }
};

const handleContractDateChange = async (val: Date[]) => {
  contract.value.start_at = useDateTime().formatDateForApi(val[0]);
  contract.value.end_at = useDateTime().formatDateForApi(val[1]);
  eventsRemovedByDateEdit.value = 0;
  eventsRemovedForNewContract.value = 0;
  if (contractStore.editType === CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT) {
    const response = await contractStore.checkEventToRemove();
    eventsRemovedByDateEdit.value = response.events_count;
  } else {
    const serviceStartDate = useDateTime().formatDateForApi(val[0]);
    const response = await contractStore.getEventsRemovedForNewContract(serviceStartDate);
    eventsRemovedForNewContract.value = response.events_count;
  }
};

const handleContractServiceDateChange = (val: Date[]) => {
  contract.value.service_start_at = useDateTime().formatDateForApi(val[0]);
  contract.value.service_end_at = useDateTime().formatDateForApi(val[1]);
  openServiceDateNotice.value = false;
  if (contractStore.editType === CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT) {
    openServiceDateNotice.value = true;
  }
};

const handleContractPaymentDateChange = (val: Date[]) => {
  if (!originalPaymentStartDate.value) originalPaymentStartDate.value = new Date(contract.value.payment_start_at);
  if (!originalPaymentEndDate.value) originalPaymentEndDate.value = new Date(contract.value.payment_end_at);
  contract.value.payment_start_at = useDateTime().formatDateForApi(val[0]);
  contract.value.payment_end_at = useDateTime().formatDateForApi(val[1]);
};

const contractDates = computed(() => {
  if (contract.value.start_at) {
    return [new Date(contract.value.start_at + ' 00:00:00'), new Date(contract.value.end_at + ' 00:00:00')];
  }
  return ['', ''];
});

const paymentDates = computed(() => {
  if (contract.value.payment_start_at) {
    return [
      new Date(contract.value.payment_start_at + ' 00:00:00'),
      new Date(contract.value.payment_end_at + ' 00:00:00'),
    ];
  }
  return ['', ''];
});

const serviceDates = computed(() => {
  if (contract.value.service_start_at && contract.value.service_end_at)
    return [
      new Date(contract.value.service_start_at + ' 00:00:00'),
      new Date(contract.value.service_end_at + ' 00:00:00'),
    ];
  return ['', ''];
});

const endDateLessThanStartDate =
  (startPointer: 'start_at' | 'service_start_at' | 'payment_start_at') => (value: string) => {
    const until = new Date(value as string);
    const start = new Date((contract.value[startPointer] as string) + ' 00:00:00');
    const untilLaterThanStart = until.valueOf() > start.valueOf();

    return untilLaterThanStart;
  };

const serviceDateStartThirtyDayCheck = (value: string) => {
  const serviceStart = new Date((value as string) + ' 00:00:00');
  const contractStart = new Date((contract.value.start_at as string) + ' 00:00:00');
  return (
    serviceStart.valueOf() <= contractStart.valueOf() &&
    useDateTime().getNumberOfDaysBetweenTwoDates(serviceStart, contractStart) <= 30
  );
};

const serviceDateEndThirtyDayCheck = (value: string) => {
  const serviceEnd = new Date((value as string) + ' 00:00:00');
  const contractEnd = new Date((contract.value.end_at as string) + ' 00:00:00');
  return (
    serviceEnd.valueOf() >= contractEnd.valueOf() &&
    useDateTime().getNumberOfDaysBetweenTwoDates(serviceEnd, contractEnd) <= 30
  );
};

const rules = computed(() => {
  return {
    start_at: {
      required: helpers.withMessage('Please select a contract start date', required),
    },
    end_at: {
      required: helpers.withMessage('Please select a contract end date', required),
      endDateLessThanStartDate: helpers.withMessage(
        'Please choose a date greater than or equal to the contact start date',
        endDateLessThanStartDate('start_at'),
      ),
    },
    service_start_at: {
      required: helpers.withMessage('Please select a service start date', required),
      isNotThirtyDaysBeforeContractStart: helpers.withMessage(
        'Service start date must be between 0 and 30 days before contract start date',
        serviceDateStartThirtyDayCheck,
      ),
    },
    service_end_at: {
      required: helpers.withMessage('Please select a service end date', required),
      endDateLessThanStartDate: helpers.withMessage(
        'Please choose a date greater than or equal to the service start date',
        endDateLessThanStartDate('service_start_at'),
      ),
      isNotThirtyDaysAfterContractStart: helpers.withMessage(
        'Service end date must be between 0 and 30 days after contract end date',
        serviceDateEndThirtyDayCheck,
      ),
    },
    payment_start_at: {
      required: helpers.withMessage('Please select a payment start date', required),
    },
    payment_end_at: {
      required: helpers.withMessage('Please select a payment end date', required),
      endDateLessThanStartDate: helpers.withMessage(
        'Please choose a date greater than or equal to the payment start date',
        endDateLessThanStartDate('payment_start_at'),
      ),
    },
    contract_identifier: {
      required: helpers.withMessage('Please provide a contract identifier', required),
      url: helpers.withMessage('Must be a valid URL starting with https(s)://', url),
    },
    salesrep: {
      required: helpers.withMessage('Please select a sales rep', required),
    },
    quote_number: {
      required: helpers.withMessage('Please provide a quote number', required),
    },
    hours_contracted: {
      requiredIfCustom: helpers.withMessage(
        'Please provide total contracted hours',
        requiredIf(selectedMainForm.value === CONTRACT_ENUMS.FORMS.HOURLY),
      ),
    },
  };
});

const v$ = useVuelidate(rules, contract, { $scope: scope.value });

const getCaseloadManagementTypes = async () => {
  caseloadsLoading.value = true;
  const caseloadTypesResponse = await therapyStore.getCaseloadManagementTypes();
  caseloadManagementTypes.value = caseloadTypesResponse.map(({ id, name }) => {
    return {
      name,
      value: id,
    };
  });
  caseloadsLoading.value = false;
};
</script>

<template>
  <div class="p-4">
    <div class="flex flex-wrap gap-4 w-full mb-4">
      <Skeleton v-if="loading" height="2.3rem" />
      <div v-if="!loading" class="flex flex-column flex-1">
        <div class="flex flex-row w-full justify-content-between info-label-container align-items-center required">
          <label for="service_dates">Contract Date Range</label>
          <Icon v-tooltip.top="toolTipText.contract" name="info" class="text-gray-400 text-base" />
        </div>
        <AppDateRangePicker
          id="contract_dates"
          :value="contractDates"
          class="org-datepicker"
          :class="{
            'p-invalid': (v$.start_at.$invalid && v$.start_at.$dirty) || (v$.end_at.$invalid && v$.end_at.$dirty),
          }"
          :disabled="!canEdit"
          @change="handleContractDateChange"
          @blur="
            () => {
              v$.start_at.$touch();
              v$.end_at.$touch();
            }
          "
        />
        <Message
          v-if="eventsRemovedByDateEdit"
          :closable="false"
          class="contract-date-notice border-primary w-full justify-content-start my-1"
          severity="warn"
          icon="none"
        >
          <div class="flex align-items-center">
            <Icon name="warning" class="confirm-dialog-icon text-color-yellow-dark text-lg align-self-start" />
            <p class="ml-2 text-color-red-dark">
              Changing the contract date range will remove
              <b>{{ eventsRemovedByDateEdit }}</b>
              scheduled events outside of
              <b>
                {{ useDateTime().formatIntlDate(contract.start_at) }} -
                {{ useDateTime().formatIntlDate(contract.end_at) }}
              </b>
              .
            </p>
          </div>
        </Message>
        <Message
          v-if="eventsRemovedForNewContract"
          :closable="false"
          class="contract-date-notice border-primary w-full justify-content-start my-1"
          severity="warn"
          icon="none"
        >
          <div class="flex align-items-center">
            <Icon name="warning" class="confirm-dialog-icon text-color-yellow-dark text-lg align-self-start" />
            <p class="ml-2 text-color-red-dark">
              Adding this contract will remove
              <b>{{ eventsRemovedForNewContract }}</b>
              scheduled events after
              <b>{{ useDateTime().formatIntlDate(contract.start_at) }}</b>
              .
            </p>
          </div>
        </Message>
        <div
          v-for="error of [...v$.start_at.$errors, ...v$.end_at.$errors]"
          :key="error.$uid"
          class="input-errors mt-1"
        >
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </div>
      <Skeleton v-if="loading" height="2.3rem" />
      <div v-if="!loading" class="flex flex-column flex-1">
        <div class="flex flex-row w-full justify-content-between info-label-container align-items-center required">
          <label for="service_dates">Service Date Range</label>
          <Icon v-tooltip.top="toolTipText.service" name="info" class="text-gray-400 text-base" />
        </div>
        <AppDateRangePicker
          id="service_dates"
          :value="serviceDates"
          class="org-datepicker"
          :class="{
            'p-invalid':
              (v$.service_start_at.$invalid && v$.service_start_at.$dirty) ||
              (v$.service_end_at.$invalid && v$.service_end_at.$dirty),
          }"
          :disabled="!canEdit"
          @change="handleContractServiceDateChange"
          @blur="
            () => {
              v$.service_start_at.$touch();
              v$.service_end_at.$touch();
            }
          "
        />
        <Message
          v-if="openServiceDateNotice"
          :closable="false"
          class="contract-date-notice border-primary w-full justify-content-start my-1"
          severity="warn"
          icon="none"
        >
          <div class="flex align-items-center">
            <Icon name="warning" class="confirm-dialog-icon text-color-yellow-dark text-lg align-self-start" />
            <p class="ml-2 text-color-red-dark">
              Service Providers will only be able to create and document events between
              <b>
                {{ useDateTime().formatIntlDate(contract.service_start_at) }} -
                {{ useDateTime().formatIntlDate(contract.service_end_at) }}
              </b>
              .
            </p>
          </div>
        </Message>
        <div
          v-for="error of [...v$.service_start_at.$errors, ...v$.service_end_at.$errors]"
          :key="error.$uid"
          class="input-errors mt-1"
        >
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </div>
    </div>

    <div class="flex flex-wrap gap-4 w-full mb-4">
      <div class="flex-1">
        <Skeleton v-if="loading" height="2.3rem" />
        <div v-else class="flex flex-column">
          <div class="flex flex-row w-full justify-content-between info-label-container align-items-center required">
            <label for="end">Payment Date Range</label>
            <Icon v-tooltip.top="toolTipText.payment" name="info" class="text-gray-400 text-base" />
          </div>
          <AppDateRangePicker
            id="payment_date_range"
            :value="paymentDates"
            class="org-datepicker"
            :class="{
              'p-invalid':
                (v$.payment_start_at.$invalid && v$.payment_start_at.$dirty) ||
                (v$.payment_end_at.$invalid && v$.payment_end_at.$dirty),
            }"
            :disabled="!canEdit"
            @change="handleContractPaymentDateChange"
            @blur="
              () => {
                v$.payment_start_at.$touch();
                v$.payment_end_at.$touch();
              }
            "
          />
          <div
            v-for="error of [...v$.payment_start_at.$errors, ...v$.payment_end_at.$errors]"
            :key="error.$uid"
            class="input-errors mt-1"
          >
            <div class="p-error">{{ error.$message }}</div>
          </div>
        </div>
      </div>
      <div class="flex-1 required">
        <label for="sales_rep">Sales Rep</label>
        <Skeleton v-if="loading" height="2.3rem" />
        <Dropdown
          v-else
          id="sales_rep"
          v-model="contract.salesrep"
          class="w-full mt-2"
          :class="{
            'p-invalid': v$.salesrep.$invalid && v$.salesrep.$dirty,
          }"
          :style="{
            backgroundColor: !contract.salesrep?.active ? '#F3F5F5' : '',
          }"
          :options="contractStore.salesReps"
          option-label="display_name"
          placeholder="Select Sales Rep"
          :disabled="!canEdit"
          @blur="v$.salesrep.$touch()"
        >
          <template #value="value">
            <div class="flex flex-row align-items-center">
              <div class="flex flex-column">
                <div v-if="value.value" class="font-bold" v-text="value.value.display_name" />
                <div v-else v-text="value.placeholder" />
              </div>
            </div>
          </template>
          <template #option="option">
            <div class="flex flex-row align-items-center">
              <div class="flex flex-column">
                <div v-text="option.option.display_name" />
              </div>
            </div>
          </template>
        </Dropdown>
        <div v-for="error of v$.salesrep.$errors" :key="error.$uid" class="input-errors mt-1">
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap gap-4 w-full">
      <div class="flex-1 required">
        <label for="quote">Quote Number</label>
        <Skeleton v-if="loading" height="2.3rem" />
        <InputText
          v-else
          id="quote"
          v-model="contract.quote_number"
          placeholder="e.g. 123456"
          class="data w-full mt-2"
          :class="{ 'p-invalid': v$.quote_number.$invalid && v$.quote_number.$dirty }"
          :disabled="!canEdit"
          @blur="v$.quote_number.$touch()"
        />
        <div v-for="error of v$.quote_number.$errors" :key="error.$uid" class="input-errors mt-1">
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </div>
      <div class="flex-1 required">
        <label for="id">Contract Identifier</label>
        <Skeleton v-if="loading" height="2.3rem" />
        <InputText
          v-else
          id="id"
          v-model="contract.contract_identifier"
          placeholder="Contract Identifier"
          class="data w-full mt-2"
          :class="{ 'p-invalid': v$.contract_identifier.$invalid && v$.contract_identifier.$dirty }"
          :disabled="!canEdit"
          @blur="v$.contract_identifier.$touch()"
        />
        <div v-for="error of v$.contract_identifier.$errors" :key="error.$uid" class="input-errors mt-1">
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </div>
    </div>
    <div v-if="selectedMainForm === CONTRACT_ENUMS.FORMS.HOURLY" class="flex flex-wrap gap-4 w-full mt-4">
      <div class="flex-1 required">
        <label for="hours">Hours Contracted</label>
        <Skeleton v-if="loading" height="2.3rem" />
        <InputNumber
          v-else
          id="hours"
          v-model="contract.hours_contracted"
          mode="decimal"
          :max-fraction-digits="2"
          :min-fraction-digits="0"
          placeholder="Hours Contracted"
          class="w-full data mt-2"
          :class="{ 'p-invalid': v$.hours_contracted.$invalid && v$.hours_contracted.$dirty }"
          :disabled="!canEdit"
          @blur="v$.hours_contracted.$touch()"
        />
        <div v-for="error of v$.hours_contracted.$errors" :key="error.$uid" class="input-errors mt-1">
          <div class="p-error">{{ error.$message }}</div>
        </div>
      </div>
      <div class="flex-1">
        <label for="caseloads">Caseload Management Type</label>
        <Skeleton v-if="caseloadsLoading" height="2.3rem" />
        <MultiSelect
          v-else
          id="caseloads"
          v-model="contract.caseloads"
          class="w-full mt-2"
          :options="caseloadManagementTypes"
          option-label="name"
          placeholder="Select Caseload Management Type(s)"
          :disabled="!canEdit"
        />
      </div>
    </div>

    <div class="field col-12 mt-4 p-0 pr-4 align-content-center">
      <Skeleton v-if="loading" height="2.3rem" />
      <Checkbox
        v-else
        id="split-caseload"
        v-model="contract.split_caseload"
        :true-value="1"
        :false-value="0"
        :binary="true"
        class="mr-2"
        :disabled="!canEdit"
      />
      <label class="mb-0 font-bold" for="split-caseload">Split Caseload Management</label>
    </div>
    <!-- EVALUATIONS -->
    <div class="field col-12 mt-4 p-3 border-round-lg bg-gray-50">
      <OrganizationContractEvaluations :scope="scope" />
    </div>
    <div class="flex flex-wrap gap-4 mt-4 w-full">
      <div class="flex-1">
        <OrganizationContractNoShow class="flex-1" :contract="contract" :scope="scope" />
      </div>
      <div class="flex-1">
        <OrganizationContractGroupEvents
          :loading="loading"
          :groups="(contract.groups as GroupEventInterface[])"
          :scope="scope"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.org-datepicker {
  max-width: initial !important;
}

.p-invalid {
  :deep(.p-component) {
    border: 2px solid $red;
  }
}

.info-label-container {
  margin-bottom: 10px;
}

:deep(.contract-date-notice) {
  .p-message-wrapper {
    padding: 0.25rem 0.25rem 0.25rem 0;
  }
}

:deep(.p-checkbox) {
  .p-checkbox-box {
    border-radius: 3px;
  }
}

:deep(.p-inputnumber) {
  .p-inputtext {
    width: 100%;
  }
}
</style>
